import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LogOut, History, Menu, X, Settings, Home, BarChart3 } from 'lucide-react';

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  if (!user) {
    return (
      <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <div className="w-8 h-8 bg-green-600 flex items-center justify-center">
              <span className="text-[#f5f5f7] font-black text-sm font-heading">LM</span>
            </div>
            <span className="font-bold text-lg tracking-tight font-heading">LeasingMalin.com</span>
          </Link>
          <div className="flex items-center gap-1 md:gap-3">
            <Link to="/negociation" data-testid="header-negociation-link" className="hidden sm:inline-block text-sm font-semibold text-green-400 hover:text-green-300 transition-[opacity,transform] duration-[180ms] px-3 md:px-4 py-2">
              Négocier mon devis
            </Link>
            <Link to="/login" data-testid="header-login-btn" className="text-sm font-semibold text-[#f5f5f7]/60 hover:text-[#f5f5f7] transition-[opacity,transform] duration-[180ms] px-2 md:px-4 py-2">
              Connexion
            </Link>
            <Link to="/register" data-testid="header-register-btn" className="btn-primary text-sm px-3 md:px-5 py-2">
              Commencer
            </Link>
          </div>
        </div>
      </header>
    );
  }

  const navLinks = [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/history', label: 'Historique', icon: History },
    ...(user?.role === 'admin' ? [
      { path: '/admin', label: 'Admin', icon: Settings },
      { path: '/admin/kpi', label: 'KPI', icon: BarChart3 },
    ] : []),
  ];

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="header-logo">
          <div className="w-8 h-8 bg-green-600 flex items-center justify-center">
            <span className="text-[#f5f5f7] font-black text-sm font-heading">LM</span>
          </div>
          <span className="font-bold text-lg tracking-tight font-heading">LeasingMalin.com</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              data-testid={`nav-${label.toLowerCase()}`}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-[opacity,transform] duration-[180ms] rounded-lg ${
                isActive(path) ? 'text-green-400 bg-green-500/15' : 'text-[#f5f5f7]/60 hover:text-[#f5f5f7] hover:bg-[#2c2c2e]'
              }`}
            >
              <Icon size={16} />
              {label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <span className="text-sm text-[#f5f5f7]/50">{user.name || user.email}</span>
          <button onClick={handleLogout} data-testid="logout-btn" className="flex items-center gap-2 text-sm font-medium text-[#f5f5f7]/50 hover:text-red-400 transition-[opacity,transform] duration-[180ms] px-3 py-1.5 border border-white/8 hover:border-red-500/40 hover:bg-red-950/30 rounded-lg">
            <LogOut size={14} />
            <span>Deconnexion</span>
          </button>
        </div>

        {/* Mobile toggle */}
        <button className="md:hidden text-[#f5f5f7]" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden bg-[#1c1c1e] border-t border-white/8 px-6 py-4">
          {navLinks.map(({ path, label, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-3 text-sm font-medium ${
                isActive(path) ? 'text-green-400' : 'text-[#f5f5f7]/60'
              }`}
            >
              <Icon size={16} />
              {label}
            </Link>
          ))}
          <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-3 text-sm text-[#f5f5f7]/50 w-full">
            <LogOut size={16} />
            Deconnexion
          </button>
        </div>
      )}
    </header>
  );
}
